import { RouteComponentProps } from "react-router";
import { AuthManager } from "../../component/auth/AuthContext";
import * as React from "react";
import { Component } from "react";
import LetterBadgeManager, { getLabelForIndex } from "../../service/LetterBadgeManager";
import EvalWorkflowApi, { EvalObs, EvalPass, EvalStudy, EvalWorkflow } from "../../api/EvalWorkflowApi";
import MessageService from "../../service/MessageService";
import Viewer from "../../component/viewer/Viewer";
import { Image, Region, RegionType } from "../../api/StudyApi";
import { isNullOrUndefined } from "../../util/LangUtil";
import arr from "../../util/ArrayDecorator";
import GrammarApi, { ParamOption, ParamType } from "../../api/GrammarApi";
import LocalStorageService from "../../service/LocalStorageService";
import { AnnotationToolMode } from "../../component/viewer/Viewport";
import { isRectangleTooSmall } from "../../util/RectangleUtility";
import { DisplayMode } from "../../component/viewer/DisplayMode";
import { noStudyResult } from "../../component/viewer/AiDisplayPreferences";
import {
  EvalObservation,
  GroundTruthObsPanel,
  GroundTruthPatient,
  GroundTruthTags,
} from "../../component/observation/GroundTruthComponents";
import i18n from "i18next";
import ViewportImage from "../../component/viewer/ViewportImage";
import { getParamTypes } from "../../util/getParamTypes";
import { isBoneRequired } from "../../component/observation/EvalObsUtils";
import { getElementsWithUnfilledParams } from "../../util/getElementsWithUnfilledParams";
import { isLabelableImage } from "../../util/ImageUtil";
import TypedReportApi, { TypedReport } from "../../api/TypedReportApi";
import Sentence from "../../component/nlp/Sentence";

interface GroundTruthReconcileProps extends RouteComponentProps {
  authManager: AuthManager;
}

interface GroundTruthReconcileState {
  evalCode: string;
  study?: EvalStudy;
  viewportImages?: Array<ViewportImage>;
  selectedObsIdx?: number;
  bones?: Array<ParamOption>;
  /** current pass **/
  pass: EvalPass;
  typedReports?: TypedReport[];
  loading: boolean;
  paramTypesByObsTypes?: Record<string, ParamType[]>;
}

const BONE_PARAM_CODE = "BONE";

function getImages(study: EvalStudy): Array<Image> {
  return study.series.flatMap((s) => s.images);
}

export default class GroundTruthReconcileWorkflow extends Component<
  GroundTruthReconcileProps,
  GroundTruthReconcileState
> {
  private evalWorkflowApi: EvalWorkflowApi;
  private grammarApi: GrammarApi;
  private observationTagService: LetterBadgeManager;
  private typedReportApi: TypedReportApi;

  constructor(props: GroundTruthReconcileProps, context: any) {
    super(props, context);

    this.evalWorkflowApi = EvalWorkflowApi.with(props.authManager.cancelableClient());
    this.grammarApi = GrammarApi.with(props.authManager.cancelableClient());
    this.typedReportApi = TypedReportApi.with(props.authManager.cancelableClient());

    this.observationTagService = new LetterBadgeManager();

    const params = new URLSearchParams(props.location.search);

    this.state = {
      evalCode: params.get("evalCode"),
      pass: GroundTruthReconcileWorkflow.initPass(),
      loading: true,
    };
  }

  componentDidMount(): void {
    this.nextStudy();

    LocalStorageService.getParamTypeAsync(BONE_PARAM_CODE, this.grammarApi).then((pt) => {
      const boneOpt = pt.options.sort((opt1, opt2) => {
        return opt1.label > opt2.label ? 1 : -1;
      });

      this.setState({ bones: boneOpt });
    });
  }

  componentWillUnmount(): void {
    this.evalWorkflowApi.cancel();
    this.grammarApi.cancel();
    this.typedReportApi.cancel();
  }

  private static initPass(): EvalPass {
    return { workflow: EvalWorkflow.GT_REC, observations: [], markers: null, tags: [] };
  }

  private nextStudy(): void {
    this.setState({ loading: true });

    this.evalWorkflowApi
      .findNextStudy(EvalWorkflow.GT_REC, this.state.evalCode)
      .then((rsp) => {
        const study = rsp.data;

        if (!study) {
          this.setState({ study: null, loading: false });
          return;
        }

        getParamTypes(study.gtParamsByObsType, this.grammarApi, study.obsTypes).then((paramTypesByObsTypes) => {
          this.setState({ paramTypesByObsTypes });
        });

        const viewportImages = study.series
          .flatMap((serie) => serie.images)
          .map((image) => new ViewportImage(image, []));

        this.consolidatePasses(study.passes);
        const passes = study.passes.filter((pass) => pass.workflow === EvalWorkflow.GT);
        ViewportImage.addPassesToImage(passes, viewportImages);

        this.setState({
          study,
          viewportImages: viewportImages,
          selectedObsIdx: null,
          pass: GroundTruthReconcileWorkflow.initPass(),
        });

        if (this.canReadReports()) {
          this.typedReportApi.findByStudyId(study.studyId).then((typedReports) => this.setState({ typedReports }));
        }
      })
      .catch((err) => {
        this.setState({ loading: false });
        if (err.response) {
          console.log(err);
          MessageService.error(i18n.t("error.occurred"));
        }
      });
  }

  private saveRegion(region: Region) {
    if (
      !isLabelableImage(
        this.state.study.labelableImageTags,
        this.state.viewportImages.filter((image) => image.id == region.imageId).flatMap((image) => image.image.tags)
      )
    ) {
      MessageService.error(i18n.t("error.addRegionOnRestrictTag"));
      this.refresh(this.state.study);
      return;
    }

    if (isRectangleTooSmall(region)) {
      MessageService.error(i18n.t("error.boxTooSmall"));
      this.refresh(this.state.study);
      return;
    }

    if (isNullOrUndefined(this.state.selectedObsIdx) || this.state.selectedObsIdx < 0) {
      MessageService.error(i18n.t("error.noObs"));
      // refresh study to avoid unsaved region stays on the viewports
      this.refresh(this.state.study);
      return;
    }

    // Creation time
    if (isNullOrUndefined(region.obsIdx)) {
      region.obsIdx = this.state.selectedObsIdx;
    }

    const obs = this.state.pass.observations[region.obsIdx];

    region._type = RegionType.OBSERVATION_GT;
    region.obsType = obs.obsType;

    obs.regions = obs.regions || [];
    const regions = obs.regions;

    const regionIdx = regions.indexOf(region);
    if (regionIdx < 0) {
      regions.push(region);
    }

    this.refresh(this.state.study);
  }

  private deleteRegion(region: Region) {
    if (region && !isNullOrUndefined(region.obsIdx)) {
      const study = this.state.study;

      const obs = this.state.pass.observations[region.obsIdx];
      arr(obs.regions).remove((r) => r === region);

      this.refresh(study);
    }
  }

  private validate() {
    const obsWithNoRegion = this.state.pass.observations.filter((obs) => obs.regions.length <= 0);

    if (obsWithNoRegion.length > 0) {
      obsWithNoRegion.forEach((obs) => {
        MessageService.error(i18n.t("error.noRegion") + obs._badge.label);
      });
      return;
    }

    const uncompleteObs = getElementsWithUnfilledParams(this.state.pass.observations, this.state.paramTypesByObsTypes);

    if (uncompleteObs.length > 0) {
      uncompleteObs.forEach((obs) => {
        MessageService.error(i18n.t("error.noParam") + obs._badge.label);
      });
      return;
    }

    this.evalWorkflowApi.savePass(this.state.evalCode, this.state.study.id, this.state.pass).then(() => {
      MessageService.info(i18n.t("global.saved"));
      this.nextStudy();
    });
  }

  private returnToWelcomePage() {
    this.props.history.push("/ground-truth");
  }

  private selectObs(idx: number) {
    this.state.viewportImages.forEach((img) => {
      img.resetAttenuation();
      img.attenuateOtherRegions(idx);
    });

    this.setState({ selectedObsIdx: idx });
  }

  private deleteObs(idx: number) {
    const obs = this.state.pass.observations[idx];

    const selectedObs =
      this.state.selectedObsIdx != null ? this.state.pass.observations[this.state.selectedObsIdx] : null;

    this.observationTagService.unsetTagForObs(idx);

    if (this.state.selectedObsIdx === idx) {
      this.setState({ selectedObsIdx: null });
    }

    arr(this.state.pass.observations).remove((o) => o === obs);

    let selectedObsIdx = null;

    this.refresh(this.state.study);

    if (selectedObs) {
      selectedObsIdx = arr(this.state.pass.observations).findFirstIndex((o) => o === selectedObs);
    }

    this.selectObs(selectedObsIdx);
  }

  private createObs(obsType: string, bone: ParamOption) {
    if (isBoneRequired(obsType) && !bone?.code) {
      MessageService.error(i18n.t("error.selectBone"));
    }

    const newObs = { regions: [], obsType: obsType, boneCode: bone?.code } as EvalObs;

    this.state.pass.observations.push(newObs);

    const idx = this.state.pass.observations.indexOf(newObs);

    this.refresh(this.state.study);
    this.setState({ selectedObsIdx: idx });
  }

  private assignObsTags(pass: EvalPass) {
    pass.observations.forEach((obs, idx) => {
      obs._badge = this.observationTagService.getLetterBadgeRandomColor(String(idx));
    });
  }

  private refresh(study: EvalStudy, prevImages = this.state.viewportImages): void {
    this.observationTagService.reset();
    this.assignObsTags(this.state.pass);

    const viewportImages = ViewportImage.fromObservations(
      this.state.pass.observations,
      getImages(this.state.study),
      prevImages,
      RegionType.OBSERVATION_GT
    );

    ViewportImage.addPassesToImage(study.passes, viewportImages);

    this.setState({ study, viewportImages: viewportImages });
  }

  private consolidatePasses(evalPasses: Array<EvalPass>) {
    evalPasses.forEach((pass, passIdx) => {
      pass._passIdx = passIdx;
      pass._passColor = ViewportImage.getPassColor(passIdx);

      pass.observations.map((obs, idx) => {
        obs._badge = { label: getLabelForIndex(idx), color: pass._passColor };
      });
    });
  }

  private onTagsChanged(newTags: Array<string>) {
    this.setState((prevState) => {
      return { pass: { ...prevState.pass, tags: newTags } };
    });
  }

  private onImageLoaded() {
    this.setState({ loading: false });
  }

  onParamChange = (index: number, paramCode: string, value: string) => {
    this.setState(({ pass }) => ({
      pass: {
        ...pass,
        observations: pass.observations.map((obs, idx) => {
          if (index !== idx) {
            return obs;
          }
          return {
            ...obs,
            valueByParam: {
              ...(obs.valueByParam || {}),
              [paramCode]: value,
            },
          };
        }),
      },
    }));
  };

  render(): React.ReactNode {
    return (
      <div className="ground-truth-workflow-rec">
        <div className="container-fluid">
          {this.state.loading && <p>{i18n.t("global.loading")}</p>}

          {!this.state.loading && !this.state.study && <p>{i18n.t("global.noMoreCases")}</p>}
        </div>

        {this.state.study && (
          <Viewer
            study={this.state.study}
            annotationToolMode={AnnotationToolMode.REGION}
            observations={this.state.pass.observations}
            authManager={this.props.authManager}
            images={this.state.viewportImages}
            saveRegion={(r) => this.saveRegion(r)}
            deleteRegion={(r) => this.deleteRegion(r)}
            display={DisplayMode.gtReconcileWorkflow()}
            aiDisplayPreferences={noStudyResult()}
            onImageLoaded={() => this.onImageLoaded()}
            actions={() => (
              <div className="list-inline">
                <div className="list-inline-item btn-group ">
                  <button className="btn btn-primary" disabled={this.state.loading} onClick={(evt) => this.validate()}>
                    {this.state.loading && <i className="fa fa-spinner fa-spin" />}
                    {!this.state.loading && <span>{i18n.t("global.validate")}</span>}
                  </button>

                  <button className="btn btn-default close-btn" onClick={(evt) => this.returnToWelcomePage()}>
                    {i18n.t("global.close")} <i className="fa fa-times" />
                  </button>
                </div>
              </div>
            )}
            aside={() => (
              <div>
                <GroundTruthTags
                  tags={this.state.pass.tags}
                  studyId={this.state.study.studyId}
                  onChange={(newTags) => this.onTagsChanged(newTags)}
                />

                <GroundTruthPatient patientAge={this.state.study.patientAge} patientSex={this.state.study.patientSex} />

                <GroundTruthObsPanel
                  obsTypes={this.state.study.obsTypes}
                  bones={this.state.bones}
                  pass={this.state.pass}
                  selectedObsIdx={this.state.selectedObsIdx}
                  paramTypesByObsTypes={this.state.paramTypesByObsTypes}
                  onCreate={(obsType, bone) => this.createObs(obsType, bone)}
                  onSelect={(idx) => this.selectObs(idx)}
                  onDelete={(idx) => this.deleteObs(idx)}
                  onParamChange={this.onParamChange}
                />

                {this.canReadReports() && (
                  <div className="card">
                    <div className="card-title">{i18n.t("global.typedReport")}</div>
                    <div className="card-body">
                      {this.state.typedReports &&
                        this.state.typedReports.map((report) => (
                          <div key={report.id}>
                            {report.sentences &&
                              report.sentences.map((sentence, si) => <Sentence key={si} value={sentence} />)}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {this.state.study.passes?.map((pass, passIdx) => (
                  <div className="card" key={pass._passIdx}>
                    <div className="card-title" style={{ color: pass._passColor }}>
                      {i18n.t("global.pass")} {pass._passIdx}
                    </div>
                    <div className="card-body">
                      <div>
                        {pass.tags.map((tag) => (
                          <span className="badge badge-info mr-1" key={tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                      {pass.observations.map((obs, obsIdx) => (
                        <EvalObservation key={obsIdx} obs={obs} bones={this.state.bones} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          />
        )}
      </div>
    );
  }

  private canReadReports() {
    return this.props.authManager.hasRole("ADMIN") || this.state.study.gtWithReports;
  }
}
